/**
 * Route-aware Suspense fallback skeletons.
 *
 * Why this exists
 * ---------------
 * The default Suspense fallback was a tiny centred spinner — fine for a
 * dialog, jarring for a full page. According to UX research (Nielsen
 * Norman Group, 2020) skeleton screens that mirror the upcoming layout
 * are perceived as ~40% faster than indeterminate spinners because the
 * brain interprets stable layout as "progress".
 *
 * Implementation
 * --------------
 * Each skeleton matches the structural skeleton of the real page
 * (header strip, filter row, table rows or cards). When the lazy chunk
 * finishes loading the real component slots into the same shape, so the
 * transition is visually quiet.
 *
 * All skeletons reuse the Shadcn `Skeleton` primitive (animate-pulse
 * rounded gray block).
 */
import { useLocation } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';

// ── Generic building blocks ─────────────────────────────────────────────

function PageHeader() {
  return (
    <div className="flex items-center justify-between mb-6">
      <div className="space-y-2">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-4 w-72" />
      </div>
      <div className="flex gap-2">
        <Skeleton className="h-9 w-24" />
        <Skeleton className="h-9 w-32" />
      </div>
    </div>
  );
}

function FilterBar({ widths = [180, 140, 120] }) {
  return (
    <div className="flex flex-wrap gap-2 mb-4">
      {widths.map((w, i) => (
        <Skeleton key={i} className="h-9" style={{ width: `${w}px` }} />
      ))}
    </div>
  );
}

function TableSkeleton({ rows = 8, cols = [50, 200, 150, 120, 100, 80] }) {
  return (
    <div className="rounded-lg border bg-white overflow-hidden">
      {/* Header */}
      <div className="border-b bg-slate-50 px-4 py-3 flex gap-4">
        {cols.map((w, i) => (
          <Skeleton key={i} className="h-4" style={{ width: `${w}px` }} />
        ))}
      </div>
      {/* Body rows */}
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="border-b last:border-0 px-4 py-3 flex gap-4 items-center">
          {cols.map((w, i) => (
            <Skeleton
              key={i}
              className="h-4"
              style={{ width: `${w + ((r * 7 + i * 11) % 25 - 12)}px`, opacity: 0.7 }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

// ── Page-specific skeletons ─────────────────────────────────────────────

function DashboardSkeleton() {
  return (
    <div className="space-y-6 p-1" data-testid="skeleton-dashboard">
      <PageHeader />
      {/* Compliance banner strip */}
      <Skeleton className="h-24 w-full rounded-xl" />
      {/* Real-time panel */}
      <div className="rounded-xl border bg-white p-5 space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-4 w-28" />
        </div>
        <Skeleton className="h-2 w-full rounded-full" />
        <div className="grid sm:grid-cols-3 gap-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-20 rounded-lg" />
          ))}
        </div>
      </div>
      {/* Pending appointments card */}
      <div className="rounded-xl border bg-white p-5 space-y-3">
        <Skeleton className="h-5 w-56" />
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 py-2 border-b last:border-0">
            <Skeleton className="h-10 w-10 rounded-full" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-4 w-44" />
              <Skeleton className="h-3 w-28" />
            </div>
            <Skeleton className="h-6 w-20 rounded-full" />
          </div>
        ))}
      </div>
    </div>
  );
}

function AgendaSkeleton() {
  return (
    <div className="space-y-4 p-1" data-testid="skeleton-agenda">
      {/* Title + tabs */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-2">
          <Skeleton className="h-7 w-44" />
          <Skeleton className="h-3 w-72" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {['Lista', 'Diaria', 'Mensual', 'Semanal'].map((_, i) => (
            <Skeleton key={i} className="h-9 w-24 rounded-md" />
          ))}
        </div>
      </div>
      {/* Location banner */}
      <Skeleton className="h-16 w-full rounded-lg" />
      {/* Date selector strip */}
      <div className="flex items-center justify-between rounded-lg border bg-emerald-50/40 px-4 py-3">
        <Skeleton className="h-6 w-56" />
        <Skeleton className="h-6 w-36" />
      </div>
      {/* Stats chips */}
      <div className="flex flex-wrap gap-2">
        <Skeleton className="h-8 w-24 rounded-full" />
        <Skeleton className="h-8 w-28 rounded-full" />
        <Skeleton className="h-8 w-32 rounded-full" />
        <div className="flex-1" />
        <Skeleton className="h-9 w-32" />
        <Skeleton className="h-9 w-28" />
      </div>
      {/* Appointment list */}
      <div className="space-y-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 rounded-lg border bg-white px-4 py-3">
            <Skeleton className="h-12 w-16 rounded" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-3 w-32" />
            </div>
            <Skeleton className="h-6 w-20 rounded-full" />
            <Skeleton className="h-8 w-8 rounded" />
          </div>
        ))}
      </div>
    </div>
  );
}

function PatientsSkeleton() {
  return (
    <div className="space-y-4 p-1" data-testid="skeleton-patients">
      <PageHeader />
      {/* Alphabet bar */}
      <div className="flex gap-1 flex-wrap">
        {Array.from({ length: 14 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-8 rounded" />
        ))}
      </div>
      <FilterBar widths={[260, 180, 160, 140]} />
      <TableSkeleton rows={10} cols={[40, 220, 140, 130, 110, 90, 80]} />
      {/* Pagination */}
      <div className="flex items-center justify-between pt-2">
        <Skeleton className="h-4 w-48" />
        <div className="flex gap-1">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-8 w-8 rounded" />
          ))}
        </div>
      </div>
    </div>
  );
}

function ClinicalHistorySkeleton() {
  return (
    <div className="space-y-4 p-1" data-testid="skeleton-clinical-history">
      <PageHeader />
      <div className="grid lg:grid-cols-[280px_1fr] gap-4">
        {/* Sidebar — patient list */}
        <div className="rounded-lg border bg-white p-3 space-y-2">
          <Skeleton className="h-9 w-full" />
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="flex items-center gap-2 py-2 px-2 rounded">
              <Skeleton className="h-8 w-8 rounded-full" />
              <div className="flex-1 space-y-1">
                <Skeleton className="h-3.5 w-32" />
                <Skeleton className="h-3 w-20" />
              </div>
            </div>
          ))}
        </div>
        {/* Main — note timeline */}
        <div className="space-y-3">
          <div className="rounded-lg border bg-white p-4 space-y-2">
            <Skeleton className="h-5 w-48" />
            <Skeleton className="h-3 w-72" />
          </div>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="rounded-lg border bg-white p-4 space-y-2">
              <div className="flex justify-between">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-24" />
              </div>
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-5/6" />
              <Skeleton className="h-3 w-3/4" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SurgeriesSkeleton() {
  return (
    <div className="space-y-4 p-1" data-testid="skeleton-surgeries">
      <PageHeader />
      <FilterBar widths={[200, 160, 140]} />
      <TableSkeleton rows={8} cols={[100, 220, 160, 120, 100, 80]} />
    </div>
  );
}

function GenericPageSkeleton() {
  // Fallback for routes without a dedicated skeleton.
  return (
    <div className="space-y-4 p-1" data-testid="skeleton-generic">
      <PageHeader />
      <Skeleton className="h-32 w-full rounded-lg" />
      <TableSkeleton rows={6} />
    </div>
  );
}

// ── Route → skeleton mapping ────────────────────────────────────────────
// Order-sensitive: first prefix match wins.
const ROUTE_MAP = [
  ['/admin/dashboard', DashboardSkeleton],
  ['/admin/agenda', AgendaSkeleton],
  ['/admin/patients', PatientsSkeleton],
  ['/admin/clinical-history', ClinicalHistorySkeleton],
  ['/admin/surgeries', SurgeriesSkeleton],
  ['/admin/presupuestos', SurgeriesSkeleton],
  ['/admin/surgery-budgets', SurgeriesSkeleton],
  ['/admin/appointments', AgendaSkeleton],
  ['/admin/waitlist', AgendaSkeleton],
  ['/admin/', GenericPageSkeleton],   // any other /admin/ route
];

/**
 * Dynamic Suspense fallback that picks the right skeleton based on the
 * URL the user is navigating to. Used as the single global Suspense
 * fallback in App.js — no per-route boilerplate.
 */
export default function RouteSkeleton() {
  const { pathname } = useLocation();

  // Non-admin routes (login, booking, kiosk, public docs…) get a quiet
  // centred spinner — they're small and already feel snappy.
  if (!pathname.startsWith('/admin')) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="spinner" data-testid="route-loading-spinner" />
      </div>
    );
  }

  const entry = ROUTE_MAP.find(([prefix]) => pathname.startsWith(prefix));
  const SkeletonComp = entry ? entry[1] : GenericPageSkeleton;
  return <SkeletonComp />;
}
